import { useState, useEffect } from "react";
import { Check, X, Loader2, Camera, AlertTriangle, Github, Linkedin, Globe, Plus, Trash2, UserPen } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMe } from "../../hooks/useUser";
import { useAvatarUpload } from "../../hooks/useAvatarUpload";
import {
  useUsernameSetup,
  type UsernameValidationStatus,
} from "../../hooks/useUsernameSetup";
import { useAuth } from "../../contexts/AuthContext";
import { useUIStore } from "../../stores/uiStore";
import type { UpdateProfileInput } from "../../types/api";

// Social link URL patterns
const SOCIAL_PATTERNS = {
  github: { base: "https://github.com/", pattern: /^(?:https?:\/\/)?(?:www\.)?github\.com\/([^\/\s]+)\/?$/i },
  linkedin: { base: "https://linkedin.com/in/", pattern: /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([^\/\s]+)\/?$/i },
  twitter: { base: "https://x.com/", pattern: /^(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/([^\/\s]+)\/?$/i },
  bluesky: { base: "https://bsky.app/profile/", pattern: /^(?:https?:\/\/)?(?:www\.)?bsky\.app\/profile\/([^\/\s]+)\/?$/i },
};

// Extract username from URL or return as-is if already a username
function extractUsername(url: string, type: keyof typeof SOCIAL_PATTERNS): string {
  if (!url) return "";
  const match = url.match(SOCIAL_PATTERNS[type].pattern);
  if (match) return match[1];
  // If it doesn't match the URL pattern, assume it's already a username
  // Strip @ if present
  return url.replace(/^@/, "").trim();
}

// Convert username to full URL
function toFullUrl(username: string, type: keyof typeof SOCIAL_PATTERNS): string {
  if (!username) return "";
  const clean = username.replace(/^@/, "").trim();
  return SOCIAL_PATTERNS[type].base + clean;
}

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (updates: { displayName?: string; bio?: string; avatarUrl?: string }) => void;
}

export default function EditProfileModal({ open, onClose, onSave }: EditProfileModalProps) {
  const { user, updateProfile } = useMe();
  const { updateUserProfile } = useAuth();
  const { uploadAvatar, uploading: uploadingAvatar } = useAvatarUpload();
  const notifyProfileSubscribers = useUIStore((s) => s.notifyProfileSubscribers);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Social links state (stores usernames, not full URLs)
  const [githubUsername, setGithubUsername] = useState("");
  const [linkedinUsername, setLinkedinUsername] = useState("");
  const [twitterUsername, setTwitterUsername] = useState("");
  const [blueskyUsername, setBlueskyUsername] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState(""); // Portfolio keeps full URL

  // Track which social links are visible in the form
  type SocialLinkType = "github" | "linkedin" | "twitter" | "bluesky" | "portfolio";
  const [visibleSocialLinks, setVisibleSocialLinks] = useState<Set<SocialLinkType>>(new Set());

  // Username validation with availability checking
  // Pass current username as skipUsername so it doesn't show as "taken"
  const {
    username,
    setUsername,
    status: usernameStatus,
    error: usernameError,
    isValid: isUsernameValid,
  } = useUsernameSetup(user?.username, user?.username);

  // Track if values have changed from original (compare usernames extracted from stored URLs)
  const usernameChanged = username !== user?.username;
  const displayNameChanged = displayName !== (user?.displayName || "");
  const bioChanged = bio !== (user?.bio || "");
  const githubChanged = githubUsername !== extractUsername(user?.githubUrl || "", "github");
  const linkedinChanged = linkedinUsername !== extractUsername(user?.linkedinUrl || "", "linkedin");
  const twitterChanged = twitterUsername !== extractUsername(user?.twitterUrl || "", "twitter");
  const blueskyChanged = blueskyUsername !== extractUsername(user?.blueskyUrl || "", "bluesky");
  const portfolioUrlChanged = portfolioUrl !== (user?.portfolioUrl || "");
  const socialLinksChanged = githubChanged || linkedinChanged || twitterChanged || blueskyChanged || portfolioUrlChanged;
  const hasChanges =
    usernameChanged || displayNameChanged || bioChanged || avatarFile !== null || socialLinksChanged;

  // Reset form when modal opens
  useEffect(() => {
    if (open && user) {
      setDisplayName(user.displayName || "");
      setBio(user.bio || "");
      setUsername(user.username);
      setAvatarFile(null);
      setAvatarPreview(null);
      // Reset social links (extract usernames from stored URLs)
      const github = extractUsername(user.githubUrl || "", "github");
      const linkedin = extractUsername(user.linkedinUrl || "", "linkedin");
      const twitter = extractUsername(user.twitterUrl || "", "twitter");
      const bluesky = extractUsername(user.blueskyUrl || "", "bluesky");
      const portfolio = user.portfolioUrl || "";

      setGithubUsername(github);
      setLinkedinUsername(linkedin);
      setTwitterUsername(twitter);
      setBlueskyUsername(bluesky);
      setPortfolioUrl(portfolio);

      // Set visible social links based on what user already has
      const visible = new Set<SocialLinkType>();
      if (github) visible.add("github");
      if (linkedin) visible.add("linkedin");
      if (twitter) visible.add("twitter");
      if (bluesky) visible.add("bluesky");
      if (portfolio) visible.add("portfolio");

      // Check for auto-filled GitHub URL from OAuth (only if user doesn't have one set)
      if (!user.githubUrl) {
        const storedGithubUrl = localStorage.getItem("github_profile_url");
        if (storedGithubUrl) {
          const storedUsername = extractUsername(storedGithubUrl, "github");
          setGithubUsername(storedUsername);
          visible.add("github");
          localStorage.removeItem("github_profile_url");
        }
      }

      setVisibleSocialLinks(visible);
    }
  }, [open, user, setUsername]);

  // Handle avatar file selection
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (JPEG, PNG, WebP, GIF)
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a JPEG, PNG, WebP, or GIF image");
      return;
    }

    // Allow larger files - they'll be compressed client-side (max 20MB input)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image must be less than 20MB");
      return;
    }

    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (saving || uploadingAvatar) return;

    // If username changed, validate it's available
    if (usernameChanged && !isUsernameValid) {
      toast.error("Please choose a valid username");
      return;
    }

    setSaving(true);
    try {
      // Build profile update input
      const input: UpdateProfileInput = {};

      // Upload avatar if file selected
      if (avatarFile) {
        try {
          const avatarUrl = await uploadAvatar(avatarFile);
          input.avatarUrl = avatarUrl;
        } catch (err) {
          console.error("Failed to upload avatar:", err);
          toast.error("Failed to upload avatar");
          setSaving(false);
          return;
        }
      }

      if (displayNameChanged) {
        input.displayName = displayName || undefined;
      }

      if (bioChanged) {
        input.bio = bio || undefined;
      }

      if (usernameChanged) {
        input.username = username;
      }

      // Social links - convert usernames to full URLs, allow clearing
      if (githubChanged) {
        input.githubUrl = githubUsername ? toFullUrl(githubUsername, "github") : undefined;
      }
      if (linkedinChanged) {
        input.linkedinUrl = linkedinUsername ? toFullUrl(linkedinUsername, "linkedin") : undefined;
      }
      if (twitterChanged) {
        input.twitterUrl = twitterUsername ? toFullUrl(twitterUsername, "twitter") : undefined;
      }
      if (blueskyChanged) {
        input.blueskyUrl = blueskyUsername ? toFullUrl(blueskyUsername, "bluesky") : undefined;
      }
      if (portfolioUrlChanged) {
        input.portfolioUrl = portfolioUrl || undefined;
      }

      if (Object.keys(input).length > 0) {
        await updateProfile(input);

        // Update auth context so Navbar reflects changes immediately
        updateUserProfile({
          displayName: input.displayName,
          avatarUrl: input.avatarUrl,
          username: usernameChanged ? username : undefined,
        });
      }

      // Optimistic update for Profile page
      const updates = { displayName, bio, avatarUrl: input.avatarUrl };
      if (onSave) {
        onSave(updates);
      }
      // Notify profile subscribers to refetch (Navbar, Profile page)
      notifyProfileSubscribers();

      toast.success("Settings saved!");
      onClose();
    } catch (err) {
      console.error("Failed to save settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (
    status: UsernameValidationStatus,
    changed: boolean
  ) => {
    if (!changed) return null;

    switch (status) {
      case "checking":
        return (
          <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--muted-foreground))]" />
        );
      case "available":
        return <Check className="h-4 w-4 text-green-500" />;
      case "taken":
      case "invalid":
        return <X className="h-4 w-4 text-[hsl(var(--destructive))]" />;
      default:
        return null;
    }
  };

  const currentAvatarUrl = avatarPreview || user?.avatarUrl;

  // Helper to add a social link
  const addSocialLink = (type: SocialLinkType) => {
    setVisibleSocialLinks(prev => new Set([...prev, type]));
  };

  // Helper to remove a social link
  const removeSocialLink = (type: SocialLinkType) => {
    setVisibleSocialLinks(prev => {
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
    // Also clear the value
    switch (type) {
      case "github": setGithubUsername(""); break;
      case "linkedin": setLinkedinUsername(""); break;
      case "twitter": setTwitterUsername(""); break;
      case "bluesky": setBlueskyUsername(""); break;
      case "portfolio": setPortfolioUrl(""); break;
    }
  };

  // Get available (not yet added) social links
  const availableSocialLinks = (["github", "linkedin", "twitter", "bluesky", "portfolio"] as SocialLinkType[])
    .filter(type => !visibleSocialLinks.has(type));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[425px] max-h-[85vh] flex flex-col">
        <DialogHeader className="pb-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <DialogTitle className="font-mono flex items-center gap-2">
            <UserPen className="h-5 w-5" />
            Edit Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4 px-1 -mx-1 overflow-y-auto flex-1">
          {/* Avatar Section */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-4 border-[hsl(var(--background))] shadow-lg">
                <AvatarImage
                  src={currentAvatarUrl}
                  alt={user?.displayName || user?.username}
                />
                <AvatarFallback className="text-2xl font-semibold bg-[hsl(var(--muted))]">
                  {user?.displayName?.[0] || user?.username?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                htmlFor="avatar-upload"
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="h-6 w-6 text-white" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Click to change avatar
            </p>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              maxLength={50}
            />
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              This is how others will see your name
            </p>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 128))}
              placeholder="A short bio about yourself"
              maxLength={128}
              rows={2}
              className="resize-none"
            />
            <p className="text-xs text-[hsl(var(--muted-foreground))] text-right">
              {bio.length}/128
            </p>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="relative">
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                className="pr-10 font-mono"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {getStatusIcon(usernameStatus, usernameChanged)}
              </div>
            </div>
            {usernameError && usernameChanged && (
              <p className="text-xs text-[hsl(var(--destructive))]">
                {usernameError}
              </p>
            )}
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {username || "username"}.commit.you
            </p>
            {usernameChanged && isUsernameValid && (
              <div className="flex items-start gap-2 p-2 mt-2 rounded-md bg-yellow-500/10 border border-yellow-500/20">
                <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-600 dark:text-yellow-400">
                  Changing your username will break existing links to your profile and commits. Old URLs will no longer redirect.
                </p>
              </div>
            )}
          </div>

          {/* Social Links */}
          <div className="space-y-3">
            <Label>Social Links</Label>
            <div className="space-y-2">
              {visibleSocialLinks.has("github") && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Github className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      value={githubUsername}
                      onChange={(e) => setGithubUsername(e.target.value.replace(/^@/, ""))}
                      placeholder="username"
                      className="pl-10"
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSocialLink("github")}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </Button>
                </div>
              )}
              {visibleSocialLinks.has("linkedin") && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      value={linkedinUsername}
                      onChange={(e) => setLinkedinUsername(e.target.value.replace(/^@/, ""))}
                      placeholder="username"
                      className="pl-10"
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSocialLink("linkedin")}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </Button>
                </div>
              )}
              {visibleSocialLinks.has("twitter") && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <Input
                      value={twitterUsername}
                      onChange={(e) => setTwitterUsername(e.target.value.replace(/^@/, ""))}
                      placeholder="username"
                      className="pl-10"
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSocialLink("twitter")}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </Button>
                </div>
              )}
              {visibleSocialLinks.has("bluesky") && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.601 6.286.505 1.46 1.718 1.945 3.136 1.763 2.128-.272 4.147-.675 4.147-.675l-3.82 3.22c-2.23 1.878-3.893 4.153-3.893 5.872 0 1.368.965 2.766 3.054 2.766 2.142 0 3.967-1.666 5.063-2.88 2.127-2.357 2.94-4.03 3.712-5.853.773 1.823 1.585 3.496 3.712 5.853 1.096 1.214 2.921 2.88 5.063 2.88 2.089 0 3.054-1.398 3.054-2.766 0-1.719-1.663-3.994-3.893-5.872l-3.82-3.22s2.019.403 4.147.675c1.418.182 2.631-.303 3.136-1.763C23.622 9.418 24 4.458 24 3.768c0-.688-.139-1.86-.902-2.203-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.686 12 10.8z"/>
                    </svg>
                    <Input
                      value={blueskyUsername}
                      onChange={(e) => setBlueskyUsername(e.target.value.replace(/^@/, ""))}
                      placeholder="username.bsky.social"
                      className="pl-10"
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSocialLink("bluesky")}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </Button>
                </div>
              )}
              {visibleSocialLinks.has("portfolio") && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      value={portfolioUrl}
                      onChange={(e) => setPortfolioUrl(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      className="pl-10"
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeSocialLink("portfolio")}>
                    <Trash2 className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </Button>
                </div>
              )}

              {/* Add social link button */}
              {availableSocialLinks.length > 0 && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Add social link
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {availableSocialLinks.includes("github") && (
                      <DropdownMenuItem onClick={() => addSocialLink("github")}>
                        <Github className="mr-2 h-4 w-4" />
                        GitHub
                      </DropdownMenuItem>
                    )}
                    {availableSocialLinks.includes("linkedin") && (
                      <DropdownMenuItem onClick={() => addSocialLink("linkedin")}>
                        <Linkedin className="mr-2 h-4 w-4" />
                        LinkedIn
                      </DropdownMenuItem>
                    )}
                    {availableSocialLinks.includes("twitter") && (
                      <DropdownMenuItem onClick={() => addSocialLink("twitter")}>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        X
                      </DropdownMenuItem>
                    )}
                    {availableSocialLinks.includes("bluesky") && (
                      <DropdownMenuItem onClick={() => addSocialLink("bluesky")}>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.601 6.286.505 1.46 1.718 1.945 3.136 1.763 2.128-.272 4.147-.675 4.147-.675l-3.82 3.22c-2.23 1.878-3.893 4.153-3.893 5.872 0 1.368.965 2.766 3.054 2.766 2.142 0 3.967-1.666 5.063-2.88 2.127-2.357 2.94-4.03 3.712-5.853.773 1.823 1.585 3.496 3.712 5.853 1.096 1.214 2.921 2.88 5.063 2.88 2.089 0 3.054-1.398 3.054-2.766 0-1.719-1.663-3.994-3.893-5.872l-3.82-3.22s2.019.403 4.147.675c1.418.182 2.631-.303 3.136-1.763C23.622 9.418 24 4.458 24 3.768c0-.688-.139-1.86-.902-2.203-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.686 12 10.8z"/>
                        </svg>
                        Bluesky
                      </DropdownMenuItem>
                    )}
                    {availableSocialLinks.includes("portfolio") && (
                      <DropdownMenuItem onClick={() => addSocialLink("portfolio")}>
                        <Globe className="mr-2 h-4 w-4" />
                        Portfolio
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-[hsl(var(--border))] flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving || uploadingAvatar}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              uploadingAvatar ||
              !hasChanges ||
              (usernameChanged && !isUsernameValid)
            }
            loading={saving || uploadingAvatar}
            className="w-full sm:w-auto"
          >
            {uploadingAvatar ? "Uploading..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
